//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import {
  AdobeCompany,
  GatewayConnectionStatus,
  IAdobeAuthenticationCredentials,
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
  IGetGA4BigQueryDatasetResponse,
  IGetGA4SettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import {
  serializedGatewayFetch,
  serializedSessionFetch,
} from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const BASE_URL = '/api/settings';

function settingsFetch(endpoint: string, init?: RequestInit) {
  return serializedSessionFetch(`${BASE_URL}${endpoint}`, init);
}

const analyticsConfigGatewayFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'web_analytics_configuration_gateway',
    `/api/v1${endpoint}`,
    init,
  );
};

/**
 * Gets the current client's Google Analytics settings
 *
 * GET /api/v1/google/analytics (web_analytics_configuration_gateway)
 */
export const getGA4Settings = async (
  init?: RequestInit,
): Promise<IGetGA4SettingsResponse> => {
  const res = await analyticsConfigGatewayFetch('/google/analytics', init);
  const json = await res.json<{
    data: { connectionStatus: GatewayConnectionStatus };
  }>();
  const { connectionStatus } = json.data;
  return {
    currentPropertyId: connectionStatus.profileId,
    profileMetadata: connectionStatus.profileMetadata,
    properties: connectionStatus.profiles,
  };
};

/**
 * Gets the current client's Google BigQuery settings
 *
 * GET /api/v1/google/bigquery (web_analytics_configuration_gateway)
 */
export const getGA4BigQueryDataset = async (
  init?: RequestInit,
): Promise<IGetGA4BigQueryDatasetResponse> => {
  const res = await analyticsConfigGatewayFetch('/google/bigquery', init);
  const json = await res.json<{
    data: { bigQueryData: IGetGA4BigQueryDatasetResponse };
  }>();
  return json.data.bigQueryData;
};

/**
 * Delete the client's Google BigQuery settings
 *
 * DELETE /api/v1/google/bigquery (web_analytics_configuration_gateway)
 */
export const deleteGA4BigQueryDataset = async (
  init?: RequestInit,
): Promise<void> => {
  await analyticsConfigGatewayFetch('/google/bigquery', {
    ...init,
    method: 'DELETE',
  });
};

/**
 * Create/Update the current client's Google BigQuery settings
 *
 * PUT /api/v1/google/bigquery (web_analytics_configuration_gateway)
 */
export const subscribeAnalyticsHubListing = async (
  url: string,
  init?: RequestInit,
): Promise<void> => {
  await analyticsConfigGatewayFetch('/google/bigquery', {
    ...init,
    method: 'PUT',
    body: JSON.stringify({ url }),
  });
};

/**
 * Gets the current client's Curalate pixel info.
 *
 * GET /api/settings/pixel
 */
export const getCuralatePixelSettings = async (
  init?: RequestInit,
): Promise<IGetCuralatePixelSettingsResponse> => {
  const res = await settingsFetch('/pixel', init);
  return await res.json<IGetCuralatePixelSettingsResponse>();
};

/**
 * Gets the Google Analytics oauth url
 *
 * POST /api/v1/google/analytics/url (web_analytics_configuration_gateway)
 */
export const getGAOAuthURL = async (
  init?: RequestInit,
): Promise<{ url?: string }> => {
  const res = await analyticsConfigGatewayFetch('/google/analytics/url', {
    ...init,
    method: 'POST',
    body: JSON.stringify({
      redirectUrl: `${window.location.origin}/google/oauth`,
    }),
  });
  const json = await res.json<{ data: { url: string } }>();
  return { url: json.data.url };
};

/**
 * disconnects the clients Google Analytics account
 *
 * DELETE /api/v1/google/analytics (web_analytics_configuration_gateway)
 */
export const deauthGAAccount = async (init?: RequestInit): Promise<void> => {
  await analyticsConfigGatewayFetch('/google/analytics', {
    ...init,
    method: 'DELETE',
  });
};

/**
 * Updates the clients Google Analytics profile
 *
 * PUT /api/v1/google/analytics/profile/${profileId} (web_analytics_configuration_gateway)
 */
export const updateGAProfile = async (
  profileId: string,
  init?: RequestInit,
): Promise<void> => {
  await analyticsConfigGatewayFetch(`/google/analytics/profile/${profileId}`, {
    ...init,
    method: 'PUT',
  });
};

export async function getAdobeOAuthSettings(
  init?: RequestInit,
): Promise<IGetAdobeSettingsResponse> {
  const res = await analyticsConfigGatewayFetch('/adobe/analytics', init);
  const json = await res.json<{
    data: {
      connectionStatus: {
        globalCompanyId?: string;
        isConnected: boolean;
        reportSuiteId?: string;
      };
    };
  }>();
  return {
    isConnected: json.data.connectionStatus.isConnected,
    globalCompanyId: json.data.connectionStatus.globalCompanyId,
    reportSuiteId: json.data.connectionStatus.reportSuiteId,
  };
}

export async function connectAdobeAnalyticsOAuth(
  request: IAdobeAuthenticationCredentials,
): Promise<void> {
  const opts = { body: JSON.stringify(request), method: 'POST' };
  await analyticsConfigGatewayFetch('/adobe/analytics/oauth', opts);
}

export async function disconnectAdobeAnalyticsOAuth(): Promise<void> {
  await analyticsConfigGatewayFetch('/adobe/analytics', {
    method: 'DELETE',
  });
}

export const getAdobeCompanies = async (
  init?: RequestInit,
): Promise<AdobeCompany[]> => {
  const res = await analyticsConfigGatewayFetch(
    '/adobe/analytics/companies',
    init,
  );
  const json = await res.json<{
    data: { companies: AdobeCompany[] };
  }>();
  return json.data.companies;
};

export const updateAdobeGlobalCompanyId = async (
  globalCompanyId: string,
): Promise<void> => {
  await analyticsConfigGatewayFetch('/adobe/analytics/global-company', {
    method: 'PUT',
    body: JSON.stringify({ globalCompanyId }),
  });
};
